import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import CodeEditor from '@/components/code/CodeEditor';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { History } from 'lucide-react';
import { generateId } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { CodeSnippet } from '@/components/chat/ChatDialogs';
import { useCodeSnippets } from '@/hooks/supabase/useCodeSnippets';
import { sendMessageToModel } from '@/services/ModelService';
import { AIModel } from '@/types';
import CodeHistory from '@/components/code/CodeHistory';
import { useAppStore } from '@/store';
import { useSupabaseChatStore } from '@/hooks/useSupabaseChatStore';

const Code = () => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { user } = useAppStore();
  const { models: allModels, fetchModels } = useSupabaseChatStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('javascript');
  const { addCodeSnippet, fetchCodeSnippets, updateCodeSnippetName } = useCodeSnippets();
  const [codeSnippets, setCodeSnippets] = useState<CodeSnippet[]>([]);
  const [loadingSnippets, setLoadingSnippets] = useState(false);

  const availableModels = useMemo(() => {
    const tierMap = { free: 0, standard: 1, premium: 2, enterprise: 2 };
    const userTier = tierMap[user?.subscription || 'free'];

    return allModels.filter(m => {
      if (!m.is_active) return false;
      if (!m.capabilities?.includes('code')) return false;
      if (m.provider === 'ollama') return true;
      return tierMap[m.tier] <= userTier;
    });
  }, [allModels, user]);

  useEffect(() => {
    fetchModels('ai_models');

    if (location.state?.code) {
      setCode(location.state.code);
      if (location.state.language) setLanguage(location.state.language);
      window.history.replaceState({}, document.title);
      toast({
        title: "Code loaded from chat",
        description: `${location.state.language} code has been loaded into the editor`,
      });
    }
  }, [location, toast, fetchModels]);

  const refreshCodeSnippets = useCallback(async () => {
    setLoadingSnippets(true);
    const snippets = await fetchCodeSnippets();
    const mapped = (snippets || []).map((snippet: any) => ({
      id: snippet.id,
      chatId: snippet.chat_id,
      content: snippet.code,
      code: snippet.code,
      name: snippet.name,
      timestamp: snippet.timestamp || snippet.created_at || new Date().toISOString(),
      language: snippet.language,
      userId: snippet.user_id,
    }));
    setCodeSnippets(mapped);
    setLoadingSnippets(false);
  }, [fetchCodeSnippets]);

  useEffect(() => { refreshCodeSnippets(); }, []);
  useEffect(() => { if (showHistory) refreshCodeSnippets(); }, [showHistory, refreshCodeSnippets]);

  const handleSaveCode = async (code: string, language: string) => {
    const filename = `snippet.${language}`;
    try {
      await addCodeSnippet(code, language, filename);
      toast({
        title: "Code snippet saved",
        description: `Your ${language} code has been saved to your history.`,
      });
      if (showHistory) refreshCodeSnippets();
    } catch (err: any) {
      toast({
        title: "Error saving code",
        description: err.message || "Could not save code snippet to Supabase.",
        variant: "destructive"
      });
    }
  };

  const handleRunCode = async (code: string, language: string) => {
    toast({
      title: language === 'javascript' || language === 'typescript' ? "Running code" : "Code execution",
      description: `Executing your ${language} code...`,
    });
  };

  const handleAskForCode = async (prompt: string, language: string): Promise<string> => {
    setIsGenerating(true);

    try {
      console.log("[DEBUG] Available models:", availableModels);

      let codeModel: AIModel | undefined = undefined;

      if (user?.defaultModelApiName) {
        const normalizedApiName = user.defaultModelApiName.trim().toLowerCase();
        codeModel = availableModels.find(m =>
          m.is_active &&
          m.capabilities?.includes('code') &&
          (m.apiName?.trim().toLowerCase() === normalizedApiName ||
           m.id?.trim().toLowerCase() === normalizedApiName)
        );
        if (codeModel) {
          console.log("[DEBUG] Using user's default model with code:", codeModel.apiName);
        } else {
          console.warn("[DEBUG] User default model is not valid or lacks 'code' capability.");
        }
      }

      if (!codeModel) {
        codeModel = availableModels.find(m =>
          m.is_active && m.capabilities?.includes('code')
        );
        if (codeModel) {
          console.log("[DEBUG] Fallback to another code-capable model:", codeModel.apiName);
        } else {
          console.warn("[DEBUG] No other code-capable model found.");
        }
      }

      if (!codeModel) {
        toast({
          title: "No usable code model found",
          description: "We couldn't find any active models that support code generation.",
          variant: "destructive"
        });
        return "// No usable code model found";
      }

      const aiPrompt = `Write a complete, idiomatic ${language} code snippet for the following request:\n\n${prompt}\n\nPlease provide only the code, no explanation.`;

      const aiResponse = await sendMessageToModel([
        {
          id: generateId(),
          content: aiPrompt,
          role: 'user',
          sender: user?.id || 'user',
          timestamp: new Date().toISOString(),
        }
      ], codeModel.name, availableModels);

      if (aiResponse?.content) {
        toast({
          title: "Code generated",
          description: `Generated ${language} code based on your prompt`,
        });
        return aiResponse.content;
      } else {
        toast({
          title: "No response from AI",
          description: "The AI did not return any code.",
          variant: "destructive"
        });
        return "// No code generated";
      }
    } catch (error) {
      console.error('Error generating code:', error);
      toast({
        title: "Generation failed",
        description: "There was an error generating your code.",
        variant: "destructive"
      });
      return "// Error generating code";
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectSnippet = (snippetId: string) => {
    const snippet = codeSnippets.find(s => s.id === snippetId);
    if (snippet) {
      setCode(snippet.code);
      setLanguage(snippet.language);
      toast({
        title: "Code loaded",
        description: `${snippet.language} code snippet loaded into editor`,
      });
    }
  };

  return (
    <MainLayout>
      <div className="h-full flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Code Editor</h1>
            <p className="text-muted-foreground">Write, edit, and run code in various programming languages</p>
          </div>
          <Button variant="outline" size="sm" className="gap-1 ml-auto flex items-center" onClick={() => setShowHistory(!showHistory)}>
            <History size={16} />
            <span>{showHistory ? 'Hide History' : 'Show History'}</span>
            {codeSnippets.length > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {codeSnippets.length}
              </span>
            )}
          </Button>
        </div>

        <div className="flex flex-row h-full w-full">
          <div className="flex-1 overflow-hidden">
            <CodeEditor
              onSave={handleSaveCode}
              onRun={handleRunCode}
              onAskForCode={handleAskForCode}
              codeSnippets={codeSnippets}
              onSelectSnippet={handleSelectSnippet}
              showHistory={showHistory}
              initialCode={code}
              initialLanguage={language}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Code;